import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Card, CardContent, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, ListItemText, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import BuscarEquipo from "../components/BuscarEquipo";
import usePageTitle from "../hooks/usePageTitle";
import { assignFederatedTeam, getMyPlanningTeams, getMyTeamManagementSubscriptions } from "../services/teamManagementService";
import { getTemporadas } from "../services/temporadasService";

const ASSIGNMENT_ERROR_MESSAGES = {
  assigned_team_limit_reached:
    "Has alcanzado el máximo de equipos permitido por tu suscripción. Desasigna un equipo antes de añadir otro o revisa tu plan.",
};
const DEFAULT_ASSIGNMENT_ERROR_MESSAGE =
  "No se pudo asignar el equipo. Inténtalo de nuevo y, si el problema continúa, contacta con soporte.";

const getAssignmentErrorMessage = (error) => {
  const businessCode = [error?.code, error?.message]
    .find((value) => typeof value === "string" && ASSIGNMENT_ERROR_MESSAGES[value.trim()]);

  return businessCode
    ? ASSIGNMENT_ERROR_MESSAGES[businessCode.trim()]
    : DEFAULT_ASSIGNMENT_ERROR_MESSAGE;
};

export default function ManageTeamsPage() {
  usePageTitle("Administrar equipos");
  const [subscriptions, setSubscriptions] = useState([]);
  const [assignedTeams, setAssignedTeams] = useState([]);
  const [seasonNames, setSeasonNames] = useState({});
  const [subscriptionId, setSubscriptionId] = useState("");
  const [candidate, setCandidate] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const confirmCancelRef = useRef(null);
  const selected = subscriptions.find((subscription) => subscription.id === subscriptionId);
  const isClub = selected?.scope === "club";

  const load = useCallback(async () => {
    setLoading(true);
    const [subscriptionResult, teamResult, seasonResult] = await Promise.allSettled([
      getMyTeamManagementSubscriptions(),
      getMyPlanningTeams(),
      getTemporadas(null),
    ]);
    const loadErrors = [];

    if (subscriptionResult.status === "fulfilled") {
      setSubscriptions(subscriptionResult.value);
      setSubscriptionId((current) => current || subscriptionResult.value[0]?.id || "");
    } else {
      loadErrors.push(subscriptionResult.reason?.message || "No se pudieron cargar tus suscripciones.");
    }

    if (teamResult.status === "fulfilled") {
      setAssignedTeams(teamResult.value);
    } else {
      loadErrors.push(teamResult.reason?.message || "No se pudieron cargar tus equipos asignados.");
    }

    if (seasonResult.status === "fulfilled") {
      setSeasonNames(Object.fromEntries(
        (seasonResult.value || []).map((season) => [
          String(season.idTemporada ?? season.id),
          season.nombre ?? season.name,
        ])
      ));
    } else {
      loadErrors.push("No se pudieron cargar los nombres de las temporadas.");
    }

    setError(loadErrors.join(" "));
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const selectTeam = (
    seasonId,
    competitionId,
    groupId,
    externalTeamId,
    team,
    federationId,
    seasonName
  ) => {
    setCandidate({
      seasonId,
      seasonName: seasonNames[String(seasonId)] || seasonName || "Temporada no disponible",
      competitionId,
      groupId,
      externalTeamId,
      federationId,
      name: team.nombre,
    });
    setMessage("");
    setError("");
  };
  const assign = async () => {
    if (!candidate || !subscriptionId) return;
    setSaving(true);
    setError("");
    try {
      const result = await assignFederatedTeam({
        subscriptionId, ...candidate, recipientEmail: isClub ? recipientEmail : undefined,
      });
      if (!result.invited) {
        try {
          setAssignedTeams(await getMyPlanningTeams());
        } catch (refreshError) {
          setError(refreshError.message || "El equipo se asignó, pero no se pudo actualizar la lista.");
        }
      }
      setMessage(result.invited
        ? "Se ha reservado un cupo de la licencia de club y creado la invitación para completar el alta."
        : "El equipo se ha asignado correctamente.");
      setCandidate(null);
      setRecipientEmail("");
    } catch (assignmentError) {
      console.error("Error al asignar un equipo federativo", {
        code: assignmentError?.code,
        message: assignmentError?.message,
      });
      setError(getAssignmentErrorMessage(assignmentError));
    } finally {
      setSaving(false);
    }
  };

  return <Box className="wp-page" component="section" sx={{ maxWidth: 900, mx: "auto", p: { xs: 2, md: 4 } }}>
    <Stack spacing={3}>
      <Box className="wp-heading">
        <Typography component="h1" variant="h4" fontWeight={800}>Administrar equipos</Typography>
        <Typography color="text.secondary">
          Selecciona equipos federativos dentro de los límites de tu suscripción activa.
        </Typography>
      </Box>
      {error && <Alert role="alert" severity="error" onClose={() => setError("")}>{error}</Alert>}
      {message && <Alert severity="success" onClose={() => setMessage("")}>{message}</Alert>}
      {loading ? <CircularProgress /> : subscriptions.length === 0 ? (
        <Alert severity="info">Necesitas una suscripción activa de la que seas titular para administrar equipos.</Alert>
      ) : <>
        <TextField select fullWidth label="Suscripción" value={subscriptionId} onChange={(event) => {
          setSubscriptionId(event.target.value); setRecipientEmail(""); setCandidate(null);
        }}>
          {subscriptions.map((subscription) => (
            <MenuItem key={subscription.id} value={subscription.id}>
              {subscription.scope === "club" ? "Plan de club" : "Plan individual"} · {subscription.plan_key}
            </MenuItem>
          ))}
        </TextField>
        {isClub && <TextField
          fullWidth label="Asignar a (correo electrónico, opcional)" type="email"
          value={recipientEmail} onChange={(event) => setRecipientEmail(event.target.value)}
          helperText="Déjalo vacío para asignártelo. Si el destinatario no tiene cuenta, recibirá una invitación."
        />}
        <Card variant="outlined"><CardContent>
          <Typography variant="h6" fontWeight={800}>Tus equipos asignados</Typography>
          {assignedTeams.length === 0 ? (
            <Typography color="text.secondary" sx={{ mt: 1 }}>Todavía no tienes equipos asignados.</Typography>
          ) : (
            <List disablePadding>
              {assignedTeams.map((team) => (
                <ListItem key={team.id} disableGutters>
                  <ListItemText
                    primary={team.name}
                    secondary={`Temporada ${
                      seasonNames[String(team.season_id)] || team.temporadaNombre || "no disponible"
                    }`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent></Card>
        <Card variant="outlined"><CardContent>
          <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>Buscar equipo federativo</Typography>
          <BuscarEquipo onEquipoSeleccionado={selectTeam} />
          {candidate && <Alert severity="info" icon={<CheckCircle />} sx={{ mt: 2 }}
            action={
              <Button
                color="inherit"
                onClick={() => {
                  const isSelfAssignment = !isClub || !recipientEmail;
                  if (isSelfAssignment) {
                    setConfirmOpen(true);
                  } else {
                    assign();
                  }
                }}
                disabled={saving}
              >
                {saving ? "Guardando..." : "Asignar equipo"}
              </Button>
            }>
            {candidate.name} · temporada {candidate.seasonName}
          </Alert>}
          <Dialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            aria-labelledby="confirm-assign-title"
            aria-describedby="confirm-assign-description"
          >
            <DialogTitle id="confirm-assign-title">Confirmar asignación de equipo</DialogTitle>
            <DialogContent>
              <Typography id="confirm-assign-description" gutterBottom>
                Estás a punto de asignar <strong>{candidate?.name}</strong> como tu equipo para la
                temporada <strong>{candidate?.seasonName}</strong>.
              </Typography>
              <Typography gutterBottom>
                Ten en cuenta lo siguiente antes de continuar:
              </Typography>
              <Typography component="ul" sx={{ pl: 2 }}>
                <li>El equipo seleccionado quedará <strong>fijado</strong> como el equipo asignado durante la temporada.</li>
                <li>No podrás cambiarlo libremente ni rotar equipos para consultar otros informes.</li>
                <li>Si necesitas corregir la asignación, contacta con el administrador de tu suscripción.</li>
              </Typography>
              <Typography sx={{ mt: 1 }}>
                ¿Deseas continuar con la asignación?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                ref={confirmCancelRef}
                onClick={() => setConfirmOpen(false)}
                autoFocus
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={() => { setConfirmOpen(false); assign(); }}
                disabled={saving}
              >
                Confirmar asignación
              </Button>
            </DialogActions>
          </Dialog>
        </CardContent></Card>
      </>}
    </Stack>
  </Box>;
}
